import {
  CompletedExercisesWithAssetDto,
  BodypartType,
} from "@growth-machine-llc/stridist-api-client";

export enum BODYPART_BALANCE_OPTIONS {
  CORE = "CORE",
  ARMS = "ARMS",
  BACK = "BACK",
  GLUTES = "GLUTES",
  SHOULDERS = "SHOULDERS",
  CHEST = "CHEST",
  LEGS = "LEGS",
  FULL_BODY = "FULL BODY",
}

export const BODYPART_BALANCE_LABELS = Object.values(BODYPART_BALANCE_OPTIONS);

export const BODYPART_BALANCE_MAPPING: Record<
  string,
  BODYPART_BALANCE_OPTIONS
> = {
  [BodypartType.LOWER_LEG]: BODYPART_BALANCE_OPTIONS.LEGS,
  [BodypartType.QUADS]: BODYPART_BALANCE_OPTIONS.LEGS,
  [BodypartType.HAMSTRINGS]: BODYPART_BALANCE_OPTIONS.LEGS,
  [BodypartType.LOWER_BACK]: BODYPART_BALANCE_OPTIONS.BACK,
  [BodypartType.UPPER_BACK_AND_NECK]: BODYPART_BALANCE_OPTIONS.BACK,
  [BodypartType.MID_BACK]: BODYPART_BALANCE_OPTIONS.BACK,
  [BodypartType.CORE]: BODYPART_BALANCE_OPTIONS.CORE,
  [BodypartType.CHEST]: BODYPART_BALANCE_OPTIONS.CHEST,
  [BodypartType.SHOULDERS]: BODYPART_BALANCE_OPTIONS.SHOULDERS,
  [BodypartType.BICEPS]: BODYPART_BALANCE_OPTIONS.ARMS,
  [BodypartType.TRICEPS]: BODYPART_BALANCE_OPTIONS.ARMS,
  [BodypartType.FOREARMS]: BODYPART_BALANCE_OPTIONS.ARMS,
  [BodypartType.GLUTES]: BODYPART_BALANCE_OPTIONS.GLUTES,
  [BodypartType.HIP_AND_GROIN]: BODYPART_BALANCE_OPTIONS.GLUTES,
  [BodypartType.FULL_BODY]: BODYPART_BALANCE_OPTIONS.FULL_BODY,
  [BodypartType.NONE]: undefined,
};

export type RadarDataItem = {
  subject: string;
  value: number;
  scaledValue: number;
  fullMark: 100;
  rawCount: number;
  relatedExercises: CompletedExercisesWithAssetDto[];
};

// Minimum scaled value to ensure visibility on the radar chart
export const MIN_SCALED = 5;

export const mapExercisesToRadarData = (
  exercises: CompletedExercisesWithAssetDto[],
): RadarDataItem[] => {
  const counts: Record<string, number> = Object.fromEntries(
    BODYPART_BALANCE_LABELS.map((bp) => [bp, 0]),
  );

  const grouped: Record<string, CompletedExercisesWithAssetDto[]> =
    Object.fromEntries(BODYPART_BALANCE_LABELS.map((bp) => [bp, []]));

  exercises.forEach((ex) => {
    ex.bodyparts?.forEach((bp) => {
      const key = BODYPART_BALANCE_MAPPING[String(bp).trim()];
      if (!key) return;
      counts[key]++;
      grouped[key].push(ex);
    });
  });

  const totalOccurrences =
    Object.values(counts).reduce((s, v) => s + v, 0) || 1;

  const realPercents = BODYPART_BALANCE_LABELS.map(
    (bp) => (counts[bp] / totalOccurrences) * 100,
  );

  const maxPercent = Math.max(...realPercents, 1);

  return BODYPART_BALANCE_LABELS.map((subject, i) => {
    const percent = realPercents[i];

    const scaledValue =
      MIN_SCALED + (percent / maxPercent) * (100 - MIN_SCALED);

    return {
      subject,
      value: Math.round(percent),
      scaledValue: Math.round(scaledValue),
      fullMark: 100,
      rawCount: counts[subject],
      relatedExercises: grouped[subject],
    };
  });
};
